import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { stripMarkup } from '../../utils/narration';
import { useTranslation } from 'react-i18next';
import HTMLFlipBook from 'react-pageflip';
import AudioPlayer from '../media/AudioPlayer';
import { getApiUrl } from '../../utils/api-url';
import { useIsMobile } from '../../hooks/useIsMobile';

// react-pageflip's typings are loose; treat the default export as a component.
const FlipBook = HTMLFlipBook as any;

interface TurnBookProps {
  turns: any[];
  intro?: any;
  // Only mount the flip-book once the initial data (intro + turn history) has loaded, so
  // react-pageflip initialises once with all pages present (defaults to true if not provided).
  ready?: boolean;
}


interface Entry {
  key: string;
  narrative: string;
  imageId?: string;
  audioId?: string;
  audioKey: string;
  // Set when the server has told us no audio is coming for this turn: an unsupported language, or
  // synthesis that ran out of retries. Distinguishes "not yet" from "never", so the waiting
  // indicator stops instead of pulsing forever at a screen-reader user.
  narrationUnavailable?: boolean;
}

// A leaf is one physical page. Each entry becomes TWO leaves — a left|right spread — that
// share ONE illustration stretched across both pages; the prose sits on a plate over the art.
type Leaf =
  | { kind: 'cover' }
  | { kind: 'left'; entry: Entry }
  | { kind: 'right'; entry: Entry };

// Turns arrive newest-first in mixed camel/snake case (live socket vs DB) — normalize and
// sort chronologically so the LATEST event is always the last spread.
function buildEntries(turns: any[], intro?: any): Entry[] {
  const entries: Entry[] = [];

  if (intro && (intro.narrative || intro.imageId)) {
    entries.push({
      key: 'intro',
      narrative: stripMarkup(intro.narrative),
      imageId: intro.imageId,
      audioId: intro.audioId,
      audioKey: 'intro',
    });
  }

  [...turns]
    .sort((a, b) => (a.turnNumber ?? a.turn_number ?? 0) - (b.turnNumber ?? b.turn_number ?? 0))
    .forEach((raw, i) => {
      const key = raw.turnId || raw.id || `t${i}`;
      entries.push({
        key,
        narrative: stripMarkup(raw.actionResult || raw.action_result),
        imageId: raw.scene_image_id || raw.sceneImageId,
        audioId: raw.narration_audio_id || raw.narrationAudioId,
        narrationUnavailable: raw.narration_unavailable || raw.narrationUnavailable,
        audioKey: key,
      });
    });

  return entries;
}

// Flatten to leaves. Cover is leaf 0 (shown alone), then each entry takes two leaves so it
// lands on a left|right spread boundary (StPageFlip pairs 1-2, 3-4, … after the cover).
function buildLeaves(entries: Entry[]): Leaf[] {
  const leaves: Leaf[] = [{ kind: 'cover' }];
  for (const entry of entries) {
    leaves.push({ kind: 'left', entry });
    leaves.push({ kind: 'right', entry });
  }
  return leaves;
}

const INK = '#f3e7c8';
const PAPER = 'linear-gradient(135deg, #f6efd8 0%, #e9ddbd 100%)';

// The cover's dark-leather background as an <img> (SVG data URI). react-pageflip doesn't paint
// a page div's own background reliably, so the cover looked empty behind its title — an <img>
// paints in every state.
const COVER_BG =
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='150'>` +
    `<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>` +
    `<stop offset='0' stop-color='#3a2a1a'/><stop offset='1' stop-color='#241812'/>` +
    `</linearGradient></defs><rect width='120' height='150' fill='url(%23g)'/></svg>`,
  ).replace(/%2523/g, '%23')}`;

// A tileable grain (fractal-noise) baked as an inline SVG — gives the pages a rough, fibrous
// paper feel when laid over the art with a soft blend. Self-contained, no external asset.
const GRAIN_SVG =
  `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>` +
  `<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/>` +
  `<feColorMatrix type='saturate' values='0'/></filter>` +
  `<rect width='100%' height='100%' filter='url(%23n)'/></svg>`;
const PAPER_TEXTURE = `url("data:image/svg+xml;utf8,${encodeURIComponent(GRAIN_SVG).replace(/%2523/g, '%23')}")`;

// Thin striations that read as the edges of many stacked sheets (a page block).
const STRIA_V =
  'repeating-linear-gradient(to bottom, rgba(60,45,25,0.30) 0px, rgba(60,45,25,0.30) 1px, ' +
  'rgba(245,238,214,0.55) 1px, rgba(245,238,214,0.55) 2.5px)';
const STRIA_H =
  'repeating-linear-gradient(to right, rgba(60,45,25,0.30) 0px, rgba(60,45,25,0.30) 1px, ' +
  'rgba(245,238,214,0.55) 1px, rgba(245,238,214,0.55) 2.5px)';

// The fore-edge + top + bottom of a page, drawn as a stack of paper sheets. `outer` is the
// fore-edge side (away from the spine): 'left' for a left page, 'right' for a right page.
function PageEdges({ outer }: { outer: 'left' | 'right' }) {
  const edge = 9;
  return (
    <>
      {/* fore-edge (outer, away from the binding) */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, [outer]: 0, width: edge, pointerEvents: 'none',
        background: STRIA_V,
        boxShadow: `inset ${outer === 'left' ? '' : '-'}2px 0 4px rgba(0,0,0,0.28)`,
      }} />
      {/* top edge */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: edge, pointerEvents: 'none',
        background: STRIA_H, boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.25)',
      }} />
      {/* bottom edge */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: edge, pointerEvents: 'none',
        background: STRIA_H, boxShadow: 'inset 0 -2px 4px rgba(0,0,0,0.25)',
      }} />
    </>
  );
}

// A faint grain wash + inner vignette that sits over the illustration to rough it up.
function PaperGrain() {
  return (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none',
      backgroundImage: PAPER_TEXTURE,
      backgroundSize: '160px 160px',
      opacity: 0.28,
      mixBlendMode: 'overlay',
      boxShadow: 'inset 0 0 90px rgba(40,28,14,0.35)',
    }} />
  );
}

// The aged-paper frame laid over each leaf, stretched flush to the page edges. Its worn corner
// sits top-right in the source; the LEFT page uses a PRE-rotated (180°) copy so the corner lands
// bottom-left. We rotate the FILE rather than apply a CSS transform, because a transform on a
// child inside react-pageflip fights the flip's own 3D transform and the overlay vanishes
// mid-flip. Full opacity — the frame carries its own transparency (clear centre).
const PAPER_OVERLAY_SRC = '/textures/old-paper.png';
const PAPER_OVERLAY_SRC_180 = '/textures/old-paper-180.png';
function PaperOverlay({ rotated = false }: { rotated?: boolean }) {
  // Render as an <img>, NOT a background-image div: react-pageflip fails to paint a child
  // div's background on a settled (non-animating) left page, so the frame vanished once the
  // flip finished. An <img> paints reliably in every state (same as the scene illustration).
  return (
    <img
      src={rotated ? PAPER_OVERLAY_SRC_180 : PAPER_OVERLAY_SRC}
      alt=""
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'fill', pointerEvents: 'none', display: 'block',
      }}
    />
  );
}

// One page's picture: the matching half of the scene with the aged-paper frame BAKED ON TOP,
// composited into a single <img> via canvas. Baking the frame into the image is the only way
// it survives react-pageflip's settled-page rendering (a separate frame element vanishes once
// the left page settles) — and it naturally bends with the leaf during a flip. The scene is
// covered over the full spread (2 pages wide) and this page shows its half, so the two pages
// still read as one continuous picture.
function ArtHalf({
  apiUrl, imageId, side, frameSrc,
}: { apiUrl: string; imageId?: string; side: 'left' | 'right'; frameSrc: string }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!imageId) { setSrc(null); return; }
    let cancelled = false;
    const W = 760, H = 980; // one page, ~0.775 aspect (matches the flipbook page ratio)
    const scene = new Image();
    const frame = new Image();
    let done = 0;
    const compose = () => {
      if (cancelled || done < 2) return;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // Scene covers the full spread (2 pages wide); draw this page's half.
      const tw = 2 * W, th = H;
      const s = Math.max(tw / scene.width, th / scene.height);
      const dw = scene.width * s, dh = scene.height * s;
      const ox = (tw - dw) / 2, oy = (th - dh) / 2;
      ctx.drawImage(scene, ox - (side === 'left' ? 0 : W), oy, dw, dh);
      // Frame on top, inset a hair so its worn rim never pokes past the page edge.
      if (frame.naturalWidth) {
        const mx = Math.round(W * 0.008), my = Math.round(H * 0.008);
        ctx.drawImage(frame, mx, my, W - 2 * mx, H - 2 * my);
      }
      try { setSrc(canvas.toDataURL('image/jpeg', 0.9)); }
      catch { setSrc(`${apiUrl}/api/media/${imageId}`); }
    };
    const onDone = () => { done++; compose(); };
    scene.onload = onDone; frame.onload = onDone; frame.onerror = onDone;
    scene.onerror = () => { if (!cancelled) setSrc(null); };
    scene.src = `${apiUrl}/api/media/${imageId}`;
    frame.src = frameSrc;
    return () => { cancelled = true; };
  }, [apiUrl, imageId, side, frameSrc]);

  if (!imageId) {
    return (
      <div style={{
        position: 'absolute', inset: 0, background: PAPER,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        color: '#8a7a55', fontFamily: 'Georgia, serif', textAlign: 'center',
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.35rem' }}>🖌️</div>
        {side === 'left' ? t('book.illustration') : t('book.drawing')}
      </div>
    );
  }
  return (
    <img
      src={src || `${apiUrl}/api/media/${imageId}`}
      alt=""
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'fill', display: 'block',
      }}
    />
  );
}

// The cover background: dark leather gradient with the aged-paper frame baked on top (same
// canvas trick as the pages, so the frame survives react-pageflip and the cover matches).
function CoverBg() {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const W = 760, H = 980;
    const frame = new Image();
    const draw = () => {
      if (cancelled) return;
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      const g = ctx.createLinearGradient(0, 0, W, H);
      g.addColorStop(0, '#3a2a1a'); g.addColorStop(1, '#241812');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      if (frame.naturalWidth) {
        const mx = Math.round(W * 0.008), my = Math.round(H * 0.008);
        ctx.drawImage(frame, mx, my, W - 2 * mx, H - 2 * my);
      }
      try { setSrc(c.toDataURL('image/jpeg', 0.9)); } catch { /* keep fallback */ }
    };
    frame.onload = draw; frame.onerror = draw; frame.src = PAPER_OVERLAY_SRC;
    return () => { cancelled = true; };
  }, []);
  return (
    <img
      src={src || COVER_BG}
      alt=""
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'fill', display: 'block', zIndex: 0,
      }}
    />
  );
}

// react-pageflip requires each page to forward its ref to a real DOM node. The page must be
// fully OPAQUE and cover its whole face so the dark desk never shows through.
const Page = forwardRef<HTMLDivElement, { children: React.ReactNode; style?: React.CSSProperties }>(
  ({ children, style }, ref) => (
    <div
      ref={ref}
      style={{
        backgroundColor: '#efe4c4', // solid fallback beneath the illustration
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        ...style,
      }}
    >
      {children}
    </div>
  ),
);

export default function TurnBook({ turns, intro, ready = true }: TurnBookProps) {
  const { t } = useTranslation();
  const apiUrl = getApiUrl();
  const isMobile = useIsMobile();
  const entries = useMemo(() => buildEntries(turns, intro), [turns, intro]);
  const leaves = useMemo(() => buildLeaves(entries), [entries]);
  const bookRef = useRef<any>(null);
  const lastEntryRef = useRef<HTMLDivElement | null>(null);
  const prevEntriesRef = useRef(0);

  // Which entry's narration may auto-play — only a turn that just happened live, never on F5.
  const [liveAudioKey, setLiveAudioKey] = useState<string | null>(null);
  const prevLenRef = useRef(0);
  // On load the data arrives in pieces (game → turns → intro), each growing the book. Treat all
  // of that as the initial fill (silent instant jump to the newest page); only mark "settled"
  // shortly after mount, after which a NEW turn animates a flip and plays its voice.
  const settledRef = useRef(false);
  const openedRef = useRef(false);
  useEffect(() => {
    const timer = setTimeout(() => { settledRef.current = true; }, 1800);
    return () => clearTimeout(timer);
  }, []);

  // The book "writes itself": always keep the newest spread open. During the initial fill we
  // jump there instantly and silently; a turn that arrives live afterwards flips with a page
  // turn and lets its narration auto-play.
  // MOBILE: no flip-book — a vertical chronicle. Keep the newest entry in view; a live turn
  // (after we've opened AND settled) auto-plays its narration, same rule as the desktop book.
  useEffect(() => {
    if (!isMobile) return;
    const total = entries.length;
    if (total === 0) { prevEntriesRef.current = 0; return; }
    const grew = total > prevEntriesRef.current;
    prevEntriesRef.current = total;
    const lastEntry = entries[entries.length - 1];
    const timer = setTimeout(() => {
      lastEntryRef.current?.scrollIntoView({ behavior: openedRef.current ? 'smooth' : 'auto', block: 'start' });
      if (openedRef.current && settledRef.current && grew && lastEntry) {
        setLiveAudioKey(lastEntry.audioKey);
      }
      openedRef.current = true;
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length, isMobile]);

  useEffect(() => {
    if (isMobile) return; // desktop flip-book only
    if (!ready) return; // wait until the book is mounted with the full page set
    const total = leaves.length;
    if (total <= 1) { prevLenRef.current = total; return; }
    const grew = total > prevLenRef.current;
    prevLenRef.current = total;

    const lastEntry = entries[entries.length - 1];
    const lastSpreadIdx = total - 2 >= 0 ? total - 2 : 0;

    // A turn arriving live (after we've opened AND settled) → animate the flip + play its voice.
    if (openedRef.current && settledRef.current && grew) {
      const timer = setTimeout(() => {
        const pf = bookRef.current?.pageFlip?.();
        if (!pf) return;
        try { pf.flip(lastSpreadIdx); } catch { /* ignore */ }
        if (lastEntry) setLiveAudioKey(lastEntry.audioKey);
      }, 120);
      return () => clearTimeout(timer);
    }

    // Initial open — including a HARD reload where react-pageflip mounts/re-inits its pages
    // asynchronously and resets to page 0 after our jump. Keep re-issuing the jump until the
    // current page actually reaches the newest spread (or we give up after ~12s).
    let cancelled = false;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      const pf = bookRef.current?.pageFlip?.();
      if (pf) {
        let cur = -1;
        try { cur = pf.getCurrentPageIndex(); } catch { /* not ready */ }
        if (cur >= lastSpreadIdx) { openedRef.current = true; return; } // reached the last spread
        try { pf.turnToPage(lastSpreadIdx); } catch { /* pages not laid out yet */ }
      }
      if (tries++ < 60) setTimeout(tick, 200);
      else openedRef.current = true;
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaves.length, isMobile, ready]);

  // Wait for the initial data before mounting anything, so the flip-book mounts once with every
  // page present (react-pageflip mis-renders when pages are added while it re-initialises).
  if (!ready) {
    return (
      <div style={{
        background: PAPER, borderRadius: '14px', border: '1px solid rgba(0,0,0,0.15)',
        padding: '3rem 2rem', textAlign: 'center', color: '#6b5c3e', marginBottom: '1.5rem',
        fontFamily: 'Georgia, serif',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem', animation: 'pulse 2s infinite' }}>📖</div>
        {t('book.opening')}
      </div>
    );
  }

  if (leaves.length <= 1) {
    return (
      <div style={{
        background: PAPER, borderRadius: '14px', border: '1px solid rgba(0,0,0,0.15)',
        padding: '3rem 2rem', textAlign: 'center', color: '#6b5c3e', marginBottom: '1.5rem',
        fontFamily: 'Georgia, serif',
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📖</div>
        {t('book.empty')}
      </div>
    );
  }

  // MOBILE: a readable vertical chronicle instead of the two-page flip-book. The illustration
  // (the same one the desktop splits across a spread) is shown whole, with the prose on a dark
  // parchment plate below it — full-width, so the text is finally comfortable on a phone.
  if (isMobile) {
    return (
      <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {entries.map((entry, i) => {
          const isLast = i === entries.length - 1;
          const dropCap = entry.narrative ? entry.narrative.charAt(0) : '';
          const rest = entry.narrative ? entry.narrative.slice(1) : '';
          return (
            <div
              key={entry.key}
              ref={isLast ? lastEntryRef : undefined}
              style={{
                borderRadius: '14px', overflow: 'hidden', background: '#2f2115',
                border: '1px solid rgba(212,180,120,0.35)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
              }}
            >
              {entry.imageId ? (
                <img
                  src={`${apiUrl}/api/media/${entry.imageId}`}
                  alt=""
                  loading="lazy"
                  style={{ width: '100%', display: 'block' }}
                />
              ) : (
                <div style={{
                  padding: '2.5rem 1rem', textAlign: 'center', color: '#8a7a55',
                  fontFamily: 'Georgia, serif', background: PAPER,
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.35rem' }}>🖌️</div>
                  {t('book.illustrationDrawing')}
                </div>
              )}

              <div style={{
                padding: '1.1rem 1.15rem', fontFamily: 'Georgia, serif', color: INK,
                background: 'linear-gradient(180deg, rgba(24,17,10,0.92) 0%, rgba(20,14,8,0.96) 100%)',
              }}>
                {entry.narrative ? (
                  <p style={{
                    margin: 0, lineHeight: 1.7, fontSize: '1.05rem', textAlign: 'left',
                    whiteSpace: 'pre-wrap', color: INK, textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                  }}>
                    <span style={{
                      float: 'left', fontSize: '3rem', lineHeight: 0.8, paddingRight: '0.5rem',
                      paddingTop: '0.2rem', fontWeight: 'bold', color: '#e6c079',
                      textShadow: '0 2px 5px rgba(0,0,0,0.9)',
                    }}>{dropCap}</span>
                    {rest}
                  </p>
                ) : (
                  <p style={{ margin: 0, color: '#c8b78a', fontStyle: 'italic' }}>…</p>
                )}

                {entry.audioId ? (
                  <div style={{ marginTop: '1rem' }}>
                    <AudioPlayer
                      key={entry.audioId}
                      audioUrl={`${apiUrl}/api/media/${entry.audioId}`}
                      autoPlay={entry.audioKey === liveAudioKey}
                      label={t('book.voiceover')}
                    />
                  </div>
                ) : entry.narrative && !entry.narrationUnavailable ? (
                  <div role="status" style={{
                    marginTop: '1rem', padding: '0.6rem 0.9rem', borderRadius: '10px',
                    background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)',
                    color: '#a3adc2', fontSize: '0.85rem', animation: 'pulse 2s infinite',
                  }}>
                    {t('book.voiceoverGenerating')}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{
      marginBottom: '1.5rem',
      display: 'flex',
      justifyContent: 'center',
      position: 'relative',
      // No frame — the book hugs its container so the illustration fills the space with no
      // perimeter margin; the drop shadow below keeps it grounded.
      filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5))',
      padding: 0,
      width: '100%',
    }}>
      <FlipBook
        ref={bookRef}
        width={620}
        height={800}
        size="stretch"
        minWidth={280}
        maxWidth={1100}
        minHeight={420}
        maxHeight={1420}
        maxShadowOpacity={0.5}
        drawShadow
        showCover
        flippingTime={900}
        usePortrait={false}
        mobileScrollSupport
        showPageCorners
        clickEventForward={false}
        style={{ margin: '0 auto', width: '100%', maxWidth: '100%' }}
      >
        {leaves.map((leaf) => {
          if (leaf.kind === 'cover') {
            return (
              <Page
                key="cover"
                style={{
                  backgroundColor: '#2f2115',
                  backgroundImage: 'linear-gradient(135deg, #3a2a1a 0%, #241812 100%)',
                  color: '#e9d8a6',
                }}
              >
                <CoverBg />
                <div style={{
                  position: 'relative', zIndex: 1,
                  height: '100%', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                  padding: '2rem', fontFamily: 'Georgia, serif',
                }}>
                  <div style={{ fontSize: '3.2rem', marginBottom: '1rem' }}>✦</div>
                  <div style={{ fontSize: '1.9rem', letterSpacing: '0.06em', lineHeight: 1.3 }}>
                    {t('book.chronicle')}
                  </div>
                  <div style={{ marginTop: '1.4rem', fontStyle: 'italic', opacity: 0.7, fontSize: '0.95rem' }}>
                    {t('book.flipHint')}
                  </div>
                </div>
              </Page>
            );
          }

          const { entry } = leaf;

          // LEFT page: the left half of the illustration only (no text).
          if (leaf.kind === 'left') {
            return (
              <Page key={`${entry.key}-L`}>
                <ArtHalf apiUrl={apiUrl} imageId={entry.imageId} side="left" frameSrc={PAPER_OVERLAY_SRC_180} />
              </Page>
            );
          }

          // RIGHT page: the right half of the illustration, with the prose on a plate over it.
          const dropCap = entry.narrative ? entry.narrative.charAt(0) : '';
          const rest = entry.narrative ? entry.narrative.slice(1) : '';
          return (
            <Page key={`${entry.key}-R`}>
              <ArtHalf apiUrl={apiUrl} imageId={entry.imageId} side="right" frameSrc={PAPER_OVERLAY_SRC} />

              {/* Translucent "плашка" over the art — fills the page from the top so the text
                  starts high with no gap above it. */}
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                padding: 'clamp(0.7rem, 3%, 1.4rem)',
              }}>
                <div style={{
                  flex: 1, minHeight: 0,
                  background: 'linear-gradient(180deg, rgba(24,17,10,0.40) 0%, rgba(24,17,10,0.52) 100%)',
                  backdropFilter: 'blur(2px)',
                  WebkitBackdropFilter: 'blur(2px)',
                  border: '1px solid rgba(212,180,120,0.45)',
                  borderRadius: '10px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
                  padding: '1rem 1.15rem',
                  overflow: 'auto',
                  fontFamily: 'Georgia, serif',
                  color: INK,
                }}>
                  {entry.narrative ? (
                    <p style={{
                      margin: 0, lineHeight: 1.7, fontSize: '1.0rem', textAlign: 'justify',
                      whiteSpace: 'pre-wrap', color: INK, textShadow: '0 1px 3px rgba(0,0,0,0.85)',
                    }}>
                      <span style={{
                        float: 'left', fontSize: '3.1rem', lineHeight: 0.8, paddingRight: '0.5rem',
                        paddingTop: '0.3rem', fontWeight: 'bold', color: '#e6c079',
                        textShadow: '0 2px 5px rgba(0,0,0,0.9)',
                      }}>{dropCap}</span>
                      {rest}
                    </p>
                  ) : (
                    <p style={{ margin: 0, color: '#c8b78a', fontStyle: 'italic' }}>…</p>
                  )}

                  {entry.audioId ? (
                    <div style={{ marginTop: '1.1rem' }}>
                      <AudioPlayer
                        key={entry.audioId}
                        audioUrl={`${apiUrl}/api/media/${entry.audioId}`}
                        autoPlay={entry.audioKey === liveAudioKey}
                        label={t('book.voiceover')}
                      />
                    </div>
                  ) : entry.narrative && !entry.narrationUnavailable ? (
                    <div role="status" style={{
                      marginTop: '1.1rem', padding: '0.6rem 0.9rem', borderRadius: '10px',
                      background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)',
                      color: '#a3adc2', fontSize: '0.85rem', animation: 'pulse 2s infinite',
                    }}>
                      {t('book.voiceoverGenerating')}
                    </div>
                  ) : null}
                </div>
              </div>
            </Page>
          );
        })}
      </FlipBook>
    </div>
  );
}
